import React, { useEffect, useMemo, useState } from 'react';

export default function MerchantCenterTest() {
  const [merchantId, setMerchantId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [statusSummary, setStatusSummary] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingAcct, setLoadingAcct] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const accountRollup = useMemo(() => {
    try {
      const products = accountStatus?.status?.products || [];
      if (!products.length) return null;
      // Prefer SurfacesAcrossGoogle (genelde nihai toplam), yoksa Shopping, yoksa ilk kayıt
      const preferredOrder = ['SurfacesAcrossGoogle', 'Shopping', 'DisplayAds'];
      const picked = (preferredOrder.map(dest => products.find(p => p?.destination === dest)).find(Boolean)) || products[0];
      const st = picked?.statistics || {};
      const active = Number(st.active || 0);
      const pending = Number(st.pending || 0);
      const disapproved = Number(st.disapproved || 0);
      const expiring = Number(st.expiring || 0);
      const total = active + pending + disapproved + expiring;
      return { active, pending, disapproved, expiring, total, sourceDestination: picked?.destination, country: picked?.country };
    } catch(_) { return null; }
  }, [accountStatus]);

  const [prodLoading, setProdLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [priceCompLoading, setPriceCompLoading] = useState(false);
  const [priceComp, setPriceComp] = useState([]);
  const [pcError, setPcError] = useState('');
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trends, setTrends] = useState([]);
  const [trendsError, setTrendsError] = useState('');
  const [trendsSnapLoading, setTrendsSnapLoading] = useState(false);
  const [trendsSnapResult, setTrendsSnapResult] = useState(null);
  const [trendsSnapError, setTrendsSnapError] = useState('');
  const [bsLoading, setBsLoading] = useState(false);
  const [bestSellers, setBestSellers] = useState([]);
  const [bsError, setBsError] = useState('');
  const [bsCategoryId, setBsCategoryId] = useState('166');
  const [bsSnapLoading, setBsSnapLoading] = useState(false);
  const [bsSnapResult, setBsSnapResult] = useState(null);
  const [bsSnapError, setBsSnapError] = useState('');

  // Competitive Visibility state
  const [cvCountry, setCvCountry] = useState('TR');
  const [cvCategoryId, setCvCategoryId] = useState('166');
  const [cvTraffic, setCvTraffic] = useState('ADS'); // ADS|ORGANIC
  const [cvStart, setCvStart] = useState(() => {
    const d = new Date(Date.now() - 6*24*60*60*1000); return d.toISOString().slice(0,10);
  });
  const [cvEnd, setCvEnd] = useState(() => new Date().toISOString().slice(0,10));
  const [cvCompetitors, setCvCompetitors] = useState([]);
  const [cvTop, setCvTop] = useState([]);
  const [cvBenchmark, setCvBenchmark] = useState([]);
  const [cvError, setCvError] = useState('');
  const [cvLoading, setCvLoading] = useState(false);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapResult, setSnapResult] = useState(null);
  const [snapError, setSnapError] = useState('');
  const [prodSnapLoading, setProdSnapLoading] = useState(false);
  const [prodSnapResult, setProdSnapResult] = useState(null);
  const [prodSnapError, setProdSnapError] = useState('');
  // CV snapshot states
  const [cvSnapLoading, setCvSnapLoading] = useState(false);
  const [cvSnapTopLoading, setCvSnapTopLoading] = useState(false);
  const [cvSnapResult, setCvSnapResult] = useState(null);
  const [cvSnapTopResult, setCvSnapTopResult] = useState(null);
  const [cvSnapError, setCvSnapError] = useState('');
  const [cvSnapTopError, setCvSnapTopError] = useState('');
  const [autoCats, setAutoCats] = useState([]);
  const [autoLog, setAutoLog] = useState([]);

  const runFetchProductsPreview = async () => {
    setErrorMsg(''); setProdLoading(true); setProducts([]);
    try {
      const brandId = getBrandId();
      const url = `https://api.glowytics.com/api/auth/merchant-center/products/preview?merchantId=${encodeURIComponent(merchantId)}&maxResults=10`;
      const resp = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId || ''
        },
        credentials: 'include'
      });
      const text = await resp.text();
      let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        setErrorMsg(json?.error || resp.statusText || 'Request failed');
      }
      const list = Array.isArray(json?.products) ? json.products : [];
      setProducts(list);
    } catch (err) {
      setErrorMsg(err?.message || 'Request error');
    } finally {
      setProdLoading(false);
    }
  };

  const runFetchPriceCompetitiveness = async () => {
    setErrorMsg(''); setPcError(''); setPriceCompLoading(true); setPriceComp([]);
    try {
      const brandId = getBrandId();
      let idsArr = products.slice(0, 10).map(p => p.id).filter(Boolean);
      const offerIdsArr = products.slice(0, 10).map(p => p.offerId).filter(Boolean);
      if (!idsArr.length) {
        // Auto-fetch preview if empty
        console.log('[MC-Test] products empty, fetching preview for IDs...');
        const previewUrl = `https://api.glowytics.com/api/auth/merchant-center/products/preview?merchantId=${encodeURIComponent(merchantId)}&maxResults=10`;
        const pr = await fetch(previewUrl, {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
          credentials: 'include'
        });
        const prText = await pr.text();
        let prJson; try { prJson = JSON.parse(prText); } catch { prJson = { raw: prText }; }
        const list = Array.isArray(prJson?.products) ? prJson.products : [];
        setProducts(list);
        idsArr = list.slice(0, 10).map(p => p.id).filter(Boolean);
        offerIdsArr.splice(0, offerIdsArr.length, ...list.slice(0, 10).map(p => p.offerId).filter(Boolean));
      }
      const ids = idsArr.join(',');
      const offerIds = offerIdsArr.join(',');
      // language/country hints from first product id if present like online:tr:TR:offer
      let hintLang = ''; let hintCountry = '';
      if (idsArr.length) {
        const parts = String(idsArr[0]).split(':');
        if (parts.length >= 4) { hintLang = parts[1] || ''; hintCountry = parts[2] || ''; }
      }
      const qp = new URLSearchParams({ merchantId, ...(ids ? { ids } : {}), ...(offerIds ? { offerIds } : {}), ...(hintLang ? { contentLanguage: hintLang } : {}), ...(hintCountry ? { targetCountry: hintCountry } : {}), });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/price-competitiveness?${qp.toString()}`;
      console.log('[MC-Test] requesting price competitiveness:', { count: idsArr.length });
      const resp = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId || ''
        },
        credentials: 'include'
      });
      const text = await resp.text();
      let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        const msg = json?.error || resp.statusText || 'Request failed';
        setPcError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      const items = Array.isArray(json?.items) ? json.items : [];
      setPriceComp(items);
    } catch (err) {
      setPcError(err?.message || 'Request error');
    } finally {
      setPriceCompLoading(false);
    }
  };

  const runFetchTopicTrends = async () => {
    setTrendsError(''); setTrends([]); setTrendsLoading(true);
    try {
      const brandId = getBrandId();
      const end = new Date();
      const start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000);
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/topic-trends?merchantId=${encodeURIComponent(merchantId)}&country=TR&startDate=${fmt(start)}&endDate=${fmt(end)}&pageSize=100`;
      const resp = await fetch(url, {
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
        credentials: 'include'
      });
      const text = await resp.text();
      let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        const msg = json?.error || resp.statusText || 'Request failed';
        setTrendsError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      setTrends(Array.isArray(json?.items) ? json.items : []);
    } catch (err) {
      setTrendsError(err?.message || 'Request error');
    } finally {
      setTrendsLoading(false);
    }
  };

  const runFetchBestSellers = async () => {
    setBsError(''); setBestSellers([]); setBsLoading(true);
    try {
      const brandId = getBrandId();
      // Try without reportDate first (backend will not force it); filter TR, WEEKLY
      const qp = new URLSearchParams({ merchantId, country: 'TR', granularity: 'WEEKLY', pageSize: '100', ...(bsCategoryId ? { categoryId: bsCategoryId } : {}) });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/best-sellers?${qp.toString()}`;
      const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
      const text = await resp.text();
      let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        const msg = json?.error || resp.statusText || 'Request failed';
        setBsError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      setBestSellers(Array.isArray(json?.items) ? json.items : []);
    } catch (err) {
      setBsError(err?.message || 'Request error');
    } finally {
      setBsLoading(false);
    }
  };

  const runSnapshotBestSellers = async () => {
    setBsSnapError(''); setBsSnapResult(null); setBsSnapLoading(true);
    try {
      const brandId = getBrandId();
      // Fetch for ALL categories: do not pass categoryId
      const qp = new URLSearchParams({ merchantId, country: 'TR', granularity: 'WEEKLY' });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/best-sellers/snapshot?${qp.toString()}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
        credentials: 'include'
      });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setBsSnapError(json?.error || resp.statusText || 'Request failed');
      setBsSnapResult(json);
    } catch (e) {
      setBsSnapError(e?.message || 'Request error');
    } finally {
      setBsSnapLoading(false);
    }
  };

  const runSnapshotTopicTrends = async () => {
    setTrendsSnapError(''); setTrendsSnapResult(null); setTrendsSnapLoading(true);
    try {
      const brandId = getBrandId();
      const end = new Date();
      const start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000);
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/topic-trends/snapshot?merchantId=${encodeURIComponent(merchantId)}&country=TR&startDate=${fmt(start)}&endDate=${fmt(end)}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
        credentials: 'include'
      });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setTrendsSnapError(json?.error || resp.statusText || 'Request failed');
      setTrendsSnapResult(json);
    } catch (e) {
      setTrendsSnapError(e?.message || 'Request error');
    } finally {
      setTrendsSnapLoading(false);
    }
  };

  const runCvCompetitors = async () => {
    setCvError(''); setCvLoading(true); setCvCompetitors([]);
    try {
      const brandId = getBrandId();
      const qp = new URLSearchParams({ merchantId, country: cvCountry, categoryId: cvCategoryId, trafficSource: cvTraffic, startDate: cvStart, endDate: cvEnd, pageSize: '100' });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/competitors?${qp.toString()}`;
      const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setCvError(json?.error || resp.statusText || 'Request failed');
      setCvCompetitors(Array.isArray(json?.items) ? json.items : []);
    } catch (e) { setCvError(e?.message || 'Request error'); } finally { setCvLoading(false); }
  };

  const runCvTop = async () => {
    setCvError(''); setCvLoading(true); setCvTop([]);
    try {
      const brandId = getBrandId();
      const qp = new URLSearchParams({ merchantId, country: cvCountry, categoryId: cvCategoryId, trafficSource: cvTraffic, startDate: cvStart, endDate: cvEnd, pageSize: '100' });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/top-merchants?${qp.toString()}`;
      const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setCvError(json?.error || resp.statusText || 'Request failed');
      setCvTop(Array.isArray(json?.items) ? json.items : []);
    } catch (e) { setCvError(e?.message || 'Request error'); } finally { setCvLoading(false); }
  };

  const runCvBenchmark = async () => {
    setCvError(''); setCvLoading(true); setCvBenchmark([]);
    try {
      const brandId = getBrandId();
      const qp = new URLSearchParams({ merchantId, country: cvCountry, categoryId: cvCategoryId, trafficSource: cvTraffic, startDate: cvStart, endDate: cvEnd, pageSize: '100' });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/benchmark?${qp.toString()}`;
      const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setCvError(json?.error || resp.statusText || 'Request failed');
      setCvBenchmark(Array.isArray(json?.items) ? json.items : []);
    } catch (e) { setCvError(e?.message || 'Request error'); } finally { setCvLoading(false); }
  };

  const runSnapshotCvCompetitors = async () => {
    setCvSnapError(''); setCvSnapResult(null); setCvSnapLoading(true);
    try {
      const brandId = getBrandId();
      const qp = new URLSearchParams({
        merchantId,
        country: cvCountry,
        categoryId: cvCategoryId,
        trafficSource: cvTraffic,
        startDate: cvStart,
        endDate: cvEnd
      });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/competitors/snapshot?${qp.toString()}`;
      const resp = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setCvSnapError(json?.error || resp.statusText || 'Request failed');
      setCvSnapResult(json);
    } catch (e) { setCvSnapError(e?.message || 'Request error'); } finally { setCvSnapLoading(false); }
  };

  const runSnapshotCvTopMerchants = async () => {
    setCvSnapTopError(''); setCvSnapTopResult(null); setCvSnapTopLoading(true);
    try {
      const brandId = getBrandId();
      const qp = new URLSearchParams({
        merchantId,
        country: cvCountry,
        categoryId: cvCategoryId,
        trafficSource: cvTraffic,
        startDate: cvStart,
        endDate: cvEnd
      });
      const url = `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/top-merchants/snapshot?${qp.toString()}`;
      const resp = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setCvSnapTopError(json?.error || resp.statusText || 'Request failed');
      setCvSnapTopResult(json);
    } catch (e) { setCvSnapTopError(e?.message || 'Request error'); } finally { setCvSnapTopLoading(false); }
  };

  // Resolve top categoryIds automatically from Best Sellers DB (recent weekly, TR)
  const resolveTopCategoryIds = async () => {
    try {
      const brandId = getBrandId();
      const build = (withDates) => {
        const params = {
          country: cvCountry || 'TR',
          granularity: 'WEEKLY',
          limit: '500' // pull enough rows to pick frequent categories
        };
        if (withDates) { params.startDate = cvStart; params.endDate = cvEnd; }
        return new URLSearchParams(params).toString();
      };
      const tryFetch = async (withDates) => {
        const url = `https://api.glowytics.com/api/google-ads/merchant-center/reports/best-sellers/db?${build(withDates)}`;
        const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
        const json = await resp.json().catch(() => ({}));
        return Array.isArray(json?.data) ? json.data : [];
      };
      // 1) Try within selected dates; 2) fallback to latest (no date filters)
      let rows = await tryFetch(true);
      if (!rows.length) { rows = await tryFetch(false); }
      const counts = {};
      for (const r of rows) {
        const raw = r.category_id || r.categoryId || '';
        const cat = (typeof raw === 'string') ? raw : String(raw || '');
        if (!/^\d+$/.test(cat)) continue; // only numeric category ids
        counts[cat] = (counts[cat] || 0) + 1;
      }
      const sorted = Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([k])=>k);
      return sorted.slice(0, 5); // top 5 categories
    } catch (_) {
      return [];
    }
  };

  const runSnapshotCvCompetitorsAuto = async () => {
    setCvSnapError(''); setCvSnapResult(null); setCvSnapLoading(true); setAutoLog([]); setAutoCats([]);
    try {
      const brandId = getBrandId();
      const cats = await resolveTopCategoryIds();
      setAutoCats(cats);
      const results = [];
      for (const cat of cats) {
        // Try with current traffic + dates
        const attempt = async (traffic, start, end) => {
          const qp = new URLSearchParams({
            merchantId,
            country: cvCountry,
            categoryId: String(cat),
            trafficSource: traffic,
            startDate: start,
            endDate: end
          });
          const url = `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/competitors/snapshot?${qp.toString()}`;
          const resp = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
          const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
          const inserted = Number(json?.result?.rowsInserted || 0);
          return { resp, json, inserted, traffic, start, end };
        };
        const altStart = (()=>{ const d = new Date(cvEnd); d.setDate(d.getDate()-28); return d.toISOString().slice(0,10); })();
        let run = await attempt(cvTraffic, cvStart, cvEnd);
        if (run.inserted === 0) run = await attempt('ORGANIC', cvStart, cvEnd);
        if (run.inserted === 0) run = await attempt(cvTraffic, altStart, cvEnd);
        results.push({ categoryId: cat, ok: run.resp.ok, result: run.json, used: { traffic: run.traffic, start: run.start, end: run.end } });
        setAutoLog(prev => [...prev, `Competitors snapshot for category ${cat} (t=${run.traffic}, ${run.start}..${run.end}): ${run.resp.ok ? `ok (${run.inserted})` : 'error'}`]);
      }
      setCvSnapResult({ auto: true, categories: cats, results });
    } catch (e) {
      setCvSnapError(e?.message || 'Request error');
    } finally {
      setCvSnapLoading(false);
    }
  };

  const runSnapshotCvTopMerchantsAuto = async () => {
    setCvSnapTopError(''); setCvSnapTopResult(null); setCvSnapTopLoading(true); setAutoLog([]); setAutoCats([]);
    try {
      const brandId = getBrandId();
      const cats = await resolveTopCategoryIds();
      setAutoCats(cats);
      const results = [];
      for (const cat of cats) {
        const attempt = async (traffic, start, end) => {
          const qp = new URLSearchParams({
            merchantId,
            country: cvCountry,
            categoryId: String(cat),
            trafficSource: traffic,
            startDate: start,
            endDate: end
          });
          const url = `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/top-merchants/snapshot?${qp.toString()}`;
          const resp = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }, credentials: 'include' });
          const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
          const inserted = Number(json?.result?.rowsInserted || 0);
          return { resp, json, inserted, traffic, start, end };
        };
        const altStart = (()=>{ const d = new Date(cvEnd); d.setDate(d.getDate()-28); return d.toISOString().slice(0,10); })();
        let run = await attempt(cvTraffic, cvStart, cvEnd);
        if (run.inserted === 0) run = await attempt('ORGANIC', cvStart, cvEnd);
        if (run.inserted === 0) run = await attempt(cvTraffic, altStart, cvEnd);
        results.push({ categoryId: cat, ok: run.resp.ok, result: run.json, used: { traffic: run.traffic, start: run.start, end: run.end } });
        setAutoLog(prev => [...prev, `Top merchants snapshot for category ${cat} (t=${run.traffic}, ${run.start}..${run.end}): ${run.resp.ok ? `ok (${run.inserted})` : 'error'}`]);
      }
      setCvSnapTopResult({ auto: true, categories: cats, results });
    } catch (e) {
      setCvSnapTopError(e?.message || 'Request error');
    } finally {
      setCvSnapTopLoading(false);
    }
  };

  const runSnapshotAccountStatus = async () => {
    setSnapError(''); setSnapResult(null); setSnapLoading(true);
    try {
      const brandId = getBrandId();
      const url = `https://api.glowytics.com/api/auth/merchant-center/accountstatus/snapshot?merchantId=${encodeURIComponent(merchantId)}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
        credentials: 'include'
      });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        setSnapError(json?.error || resp.statusText || 'Request failed');
      }
      setSnapResult(json);
    } catch (e) {
      setSnapError(e?.message || 'Request error');
    } finally {
      setSnapLoading(false);
    }
  };

  const runSnapshotAllProducts = async () => {
    setProdSnapError(''); setProdSnapResult(null); setProdSnapLoading(true);
    try {
      const brandId = getBrandId();
      const url = `https://api.glowytics.com/api/auth/merchant-center/products/snapshot?merchantId=${encodeURIComponent(merchantId)}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
        credentials: 'include'
      });
      const text = await resp.text(); let json; try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) setProdSnapError(json?.error || resp.statusText || 'Request failed');
      setProdSnapResult(json);
    } catch (e) {
      setProdSnapError(e?.message || 'Request error');
    } finally {
      setProdSnapLoading(false);
    }
  };

  const getBrandId = () => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    try {
      const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/);
      if (m) return m[0];
    } catch(_) {}
    return '';
  };

  const loadMerchantConnection = async () => {
    setErrorMsg('');
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });
      if (!resp.ok) return;
      const data = await resp.json();
      let found = null;
      if (Array.isArray(data)) {
        found = data.find(x => (x?.platform === 'MERCHANT_CENTER') || (x?.platform === 'merchant_center')) || null;
      } else if (data && typeof data === 'object') {
        const mc = data['MERCHANT_CENTER'] || data['merchant_center'] || null;
        if (mc) found = mc;
      }
      const mid = found?.customerId || found?.merchantId || '';
      if (mid) setMerchantId(String(mid));
    } catch (_) {}
  };

  useEffect(() => {
    loadMerchantConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRun = useMemo(() => !loading && !!merchantId, [loading, merchantId]);
  const canRunStatus = useMemo(() => !loadingStatus && !!merchantId, [loadingStatus, merchantId]);

  const runFetchProductCount = async () => {
    setErrorMsg(''); setResult(null); setLoading(true);
    try {
      const brandId = getBrandId();
      const url = `https://api.glowytics.com/api/auth/merchant-center/products/count?merchantId=${encodeURIComponent(merchantId)}`;
      const resp = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId || '',
        },
        credentials: 'include',
      });
      const text = await resp.text();
      let json;
      try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        setErrorMsg(json?.error || resp.statusText || 'Request failed');
        setResult(json);
      } else {
        setResult(json);
      }
    } catch (err) {
      setErrorMsg(err?.message || 'Request error');
    } finally {
      setLoading(false);
    }
  };

  const runFetchStatusSummary = async () => {
    setErrorMsg(''); setStatusSummary(null); setLoadingStatus(true);
    try {
      const brandId = getBrandId();
      const url = `https://api.glowytics.com/api/auth/merchant-center/products/status-summary?merchantId=${encodeURIComponent(merchantId)}`;
      const resp = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId || '',
        },
        credentials: 'include',
      });
      const text = await resp.text();
      let json;
      try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        setErrorMsg(json?.error || resp.statusText || 'Request failed');
        setStatusSummary(json);
      } else {
        setStatusSummary(json);
      }
    } catch (err) {
      setErrorMsg(err?.message || 'Request error');
    } finally {
      setLoadingStatus(false);
    }
  };

  const runFetchAccountStatus = async () => {
    setErrorMsg(''); setAccountStatus(null); setLoadingAcct(true);
    try {
      const brandId = getBrandId();
      const url = `https://api.glowytics.com/api/auth/merchant-center/accountstatus?merchantId=${encodeURIComponent(merchantId)}`;
      const resp = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId || '',
        },
        credentials: 'include',
      });
      const text = await resp.text();
      let json;
      try { json = JSON.parse(text); } catch { json = { raw: text }; }
      if (!resp.ok) {
        setErrorMsg(json?.error || resp.statusText || 'Request failed');
        setAccountStatus(json);
      } else {
        setAccountStatus(json);
      }
    } catch (err) {
      setErrorMsg(err?.message || 'Request error');
    } finally {
      setLoadingAcct(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Merchant Center Test</h1>
          <div className="text-sm text-gray-500">Content API for Shopping denemeleri için geçici sayfa</div>
        </div>

      {/* Snapshot to DB */}
      <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
        <div className="text-sm font-medium text-gray-800 mb-2">Snapshot: Account Status → DB</div>
        <div className="flex items-center gap-2">
          <button
            className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
            onClick={runSnapshotAccountStatus}
            disabled={!merchantId || snapLoading}
          >
            {snapLoading ? 'Saving…' : 'Save Account Status Snapshot'}
          </button>
          {snapError ? <span className="text-xs text-red-600">{snapError}</span> : null}
        </div>
        <div className="mt-3">
          <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(snapResult, null, 2)}</pre>
        </div>
      </div>

      {/* Snapshot ALL Products to DB */}
      <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
        <div className="text-sm font-medium text-gray-800 mb-2">Snapshot: All Products → DB</div>
        <div className="flex items-center gap-2">
          <button
            className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
            onClick={runSnapshotAllProducts}
            disabled={!merchantId || prodSnapLoading}
          >
            {prodSnapLoading ? 'Saving…' : 'Save All Products Snapshot'}
          </button>
          {prodSnapError ? <span className="text-xs text-red-600">{prodSnapError}</span> : null}
        </div>
        <div className="mt-3">
          <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(prodSnapResult, null, 2)}</pre>
        </div>
      </div>
        <div className="bg-white rounded-2xl border shadow-sm p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Merchant ID</label>
              <input
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                placeholder="e.g. 123456789"
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
              <div className="text-[11px] text-gray-500 mt-1">Bağlı hesap varsa otomatik dolacaktır.</div>
            </div>
            <div className="flex flex-col md:items-end gap-2">
              <button
                className={`px-4 py-2 rounded-lg text-white ${canRun ? 'bg-black hover:bg-gray-800' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={runFetchProductCount}
                disabled={!canRun}
              >
                {loading ? 'Running…' : 'Fetch Product Count'}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${canRunStatus ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={runFetchStatusSummary}
                disabled={!canRunStatus}
              >
                {loadingStatus ? 'Running…' : 'Fetch Status Summary'}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={runFetchAccountStatus}
                disabled={!merchantId || loadingAcct}
              >
                {loadingAcct ? 'Running…' : 'Fetch Account Status'}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-sky-600 hover:bg-sky-700' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={runFetchProductsPreview}
                disabled={!merchantId || prodLoading}
              >
                {prodLoading ? 'Running…' : 'Fetch 10 Products (preview)'}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-violet-600 hover:bg-violet-700' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={runFetchPriceCompetitiveness}
                disabled={!merchantId || priceCompLoading}
              >
                {priceCompLoading ? 'Running…' : 'Price Competitiveness (for 10)'}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={runFetchTopicTrends}
                disabled={!merchantId || trendsLoading}
              >
                {trendsLoading ? 'Running…' : 'Topic Trends (TR last 7 days)'}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={runFetchBestSellers}
                disabled={!merchantId || bsLoading}
              >
                {bsLoading ? 'Running…' : 'Best Sellers (TR weekly)'}
              </button>
              <div className="flex items-center gap-2">
                <input
                  value={bsCategoryId}
                  onChange={(e) => setBsCategoryId(e.target.value)}
                  placeholder="Category ID (e.g., 166)"
                  className="px-2 py-1 border rounded text-xs w-40"
                />
              </div>
            </div>
          </div>

          {errorMsg ? (
            <div className="mt-4 text-sm text-red-600">{errorMsg}</div>
          ) : null}
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5 mb-6">
          <div className="text-sm font-medium text-gray-800 mb-2">Product Count Response</div>
          <pre className="text-xs bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(result, null, 2)}</pre>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <div className="text-sm font-medium text-gray-800 mb-2">Status Summary Response</div>
          {(() => {
            const data = statusSummary || {};
            const byApproval = data.byApproval || {};
            const entries = Object.entries(byApproval);
            return (
              <>
                <div className="text-xs text-gray-600 mb-2">Toplam: {Number(data.total || 0).toLocaleString('tr-TR')}</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left">Approval Status</th>
                        <th className="px-3 py-2 text-right">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.length ? entries.map(([k,v]) => (
                        <tr key={k}>
                          <td className="px-3 py-2">{k}</td>
                          <td className="px-3 py-2 text-right">{Number(v).toLocaleString('tr-TR')}</td>
                        </tr>
                      )) : (
                        <tr><td className="px-3 py-2 text-gray-500" colSpan={2}>No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3">
                  <pre className="text-xs bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(statusSummary, null, 2)}</pre>
                </div>
              </>
            );
          })()}
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
          <div className="text-sm font-medium text-gray-800 mb-2">Account Status Response</div>
          {accountRollup ? (
            <div className="mb-3">
              <div className="text-xs text-gray-600 mb-1">Summary (destination: {accountRollup.sourceDestination || '-'}, country: {accountRollup.country || '-'})</div>
              <table className="min-w-[300px] text-xs">
                <tbody>
                  <tr><td className="pr-4 py-1 text-gray-600">Total</td><td className="py-1 text-right font-medium">{accountRollup.total.toLocaleString('tr-TR')}</td></tr>
                  <tr><td className="pr-4 py-1 text-gray-600">Active (eligible)</td><td className="py-1 text-right">{accountRollup.active.toLocaleString('tr-TR')}</td></tr>
                  <tr><td className="pr-4 py-1 text-gray-600">Pending</td><td className="py-1 text-right">{accountRollup.pending.toLocaleString('tr-TR')}</td></tr>
                  <tr><td className="pr-4 py-1 text-gray-600">Disapproved</td><td className="py-1 text-right">{accountRollup.disapproved.toLocaleString('tr-TR')}</td></tr>
                  <tr><td className="pr-4 py-1 text-gray-600">Expiring</td><td className="py-1 text-right">{accountRollup.expiring.toLocaleString('tr-TR')}</td></tr>
                </tbody>
              </table>
            </div>
          ) : null}
          <pre className="text-xs bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(accountStatus, null, 2)}</pre>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
          <div className="text-sm font-medium text-gray-800 mb-2">Products (10 preview)</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">id</th>
                  <th className="px-3 py-2 text-left">title</th>
                  <th className="px-3 py-2 text-left">brand</th>
                  <th className="px-3 py-2 text-left">availability</th>
                  <th className="px-3 py-2 text-left">price</th>
                </tr>
              </thead>
              <tbody>
                {products.length ? products.map((p, i) => (
                  <tr key={p.id || i} className="border-t">
                    <td className="px-3 py-2">{p.id}</td>
                    <td className="px-3 py-2 max-w-[260px] truncate" title={p.title}>{p.title}</td>
                    <td className="px-3 py-2">{p.brand}</td>
                    <td className="px-3 py-2">{p.availability}</td>
                    <td className="px-3 py-2">{(() => { const pr = p.price || {}; return pr.value ? `${pr.value} ${pr.currency}` : ''; })()}</td>
                  </tr>
                )) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(products, null, 2)}</pre>
          </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
          <div className="text-sm font-medium text-gray-800 mb-2">Price Competitiveness (TR)</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">id</th>
                  <th className="px-3 py-2 text-left">title</th>
                  <th className="px-3 py-2 text-right">price</th>
                  <th className="px-3 py-2 text-right">benchmark</th>
                  <th className="px-3 py-2 text-right">delta %</th>
                </tr>
              </thead>
              <tbody>
                {priceComp.length ? priceComp.map((r, i) => (
                  <tr key={r.id || i} className="border-t">
                    <td className="px-3 py-2">{r.id}</td>
                    <td className="px-3 py-2 max-w-[320px] truncate" title={r.title}>{r.title}</td>
                    <td className="px-3 py-2 text-right">{r.price?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} {r.priceCurrency}</td>
                    <td className="px-3 py-2 text-right">{r.benchmarkPrice?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} {r.benchmarkCurrency}</td>
                    <td className="px-3 py-2 text-right">{r.priceDeltaPct==null ? '-' : r.priceDeltaPct.toFixed(2)}%</td>
                  </tr>
                )) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pcError ? <div className="mt-2 text-xs text-red-600">{pcError}</div> : null}
          <div className="mt-3">
            <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(priceComp, null, 2)}</pre>
          </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
          <div className="text-sm font-medium text-gray-800 mb-2">Topic Trends (TR last 7 days)</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">date</th>
                  <th className="px-3 py-2 text-left">topic</th>
                  <th className="px-3 py-2 text-right">interest</th>
                  <th className="px-3 py-2 text-right">last7</th>
                  <th className="px-3 py-2 text-right">next7</th>
                </tr>
              </thead>
              <tbody>
                {trends.length ? trends.map((t, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2">{t.date}</td>
                    <td className="px-3 py-2 max-w-[320px] truncate" title={t.topic}>{t.topic}</td>
                    <td className="px-3 py-2 text-right">{t.searchInterest ?? '-'}</td>
                    <td className="px-3 py-2 text-right">{t.last7DaysSearchInterest ?? '-'}</td>
                    <td className="px-3 py-2 text-right">{t.next7DaysSearchInterest ?? '-'}</td>
                  </tr>
                )) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {trendsError ? <div className="mt-2 text-xs text-red-600">{trendsError}</div> : null}
          <div className="mt-3">
            <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(trends, null, 2)}</pre>
          </div>
        <div className="mt-4">
          <button
            className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-400 cursor-not-allowed'}`}
            onClick={runSnapshotTopicTrends}
            disabled={!merchantId || trendsSnapLoading}
          >
            {trendsSnapLoading ? 'Saving…' : 'Save Topic Trends Snapshot (TR last 7d)'}
          </button>
          {trendsSnapError ? <span className="ml-2 text-xs text-red-600">{trendsSnapError}</span> : null}
          <div className="mt-3">
            <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(trendsSnapResult, null, 2)}</pre>
          </div>
        </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
          <div className="text-sm font-medium text-gray-800 mb-2">Best Sellers (TR weekly)</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-right">rank</th>
                  <th className="px-3 py-2 text-left">title</th>
                  <th className="px-3 py-2 text-left">brand</th>
                  <th className="px-3 py-2 text-left">category</th>
                  <th className="px-3 py-2 text-left">rel. demand</th>
                  <th className="px-3 py-2 text-left">change</th>
                </tr>
              </thead>
              <tbody>
                {bestSellers.length ? bestSellers.map((b, i) => (
                  <tr key={`${b.rank}-${i}`} className="border-t">
                    <td className="px-3 py-2 text-right">{b.rank}</td>
                    <td className="px-3 py-2 max-w-[320px] truncate" title={b.title}>{b.title}</td>
                    <td className="px-3 py-2">{b.brand}</td>
                    <td className="px-3 py-2">{b.categoryL2 || b.categoryL1 || '-'}</td>
                    <td className="px-3 py-2">{b.relativeDemand}</td>
                    <td className="px-3 py-2">{b.relativeDemandChange}</td>
                  </tr>
                )) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={6}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {bsError ? <div className="mt-2 text-xs text-red-600">{bsError}</div> : null}
          <div className="mt-3">
            <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(bestSellers, null, 2)}</pre>
          </div>
          <div className="mt-4">
            <button
              className={`px-4 py-2 rounded-lg text-white ${merchantId ? 'bg-rose-600 hover:bg-rose-700' : 'bg-gray-400 cursor-not-allowed'}`}
              onClick={runSnapshotBestSellers}
              disabled={!merchantId || bsSnapLoading}
            >
              {bsSnapLoading ? 'Saving…' : 'Save Best Sellers Snapshot (TR weekly)'}
            </button>
            {bsSnapError ? <span className="ml-2 text-xs text-red-600">{bsSnapError}</span> : null}
            <div className="mt-3">
              <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(bsSnapResult, null, 2)}</pre>
            </div>
          </div>
        </div>

        {/* Competitive Visibility Controls */}
        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
          <div className="text-sm font-medium text-gray-800 mb-3">Competitive Visibility (country/category/date required)</div>
          <div className="flex gap-2 flex-wrap items-end mb-3 text-xs">
            <div>
              <label className="block mb-1 text-gray-600">Country</label>
              <input value={cvCountry} onChange={(e)=>setCvCountry(e.target.value.toUpperCase())} className="px-2 py-1 border rounded w-24" />
            </div>
            <div>
              <label className="block mb-1 text-gray-600">Category ID</label>
              <input value={cvCategoryId} onChange={(e)=>setCvCategoryId(e.target.value)} className="px-2 py-1 border rounded w-28" />
            </div>
            <div>
              <label className="block mb-1 text-gray-600">Traffic</label>
              <select value={cvTraffic} onChange={(e)=>setCvTraffic(e.target.value)} className="px-2 py-1 border rounded">
                <option value="ADS">ADS</option>
                <option value="ORGANIC">ORGANIC</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 text-gray-600">Start</label>
              <input type="date" value={cvStart} onChange={(e)=>setCvStart(e.target.value)} className="px-2 py-1 border rounded" />
            </div>
            <div>
              <label className="block mb-1 text-gray-600">End</label>
              <input type="date" value={cvEnd} onChange={(e)=>setCvEnd(e.target.value)} className="px-2 py-1 border rounded" />
            </div>
            <div className="flex gap-2">
              <button className={`px-3 py-2 rounded bg-indigo-600 text-white ${!merchantId? 'opacity-60':''}`} disabled={!merchantId || cvLoading} onClick={runCvCompetitors}>{cvLoading? 'Running…':'Competitors'}</button>
              <button className={`px-3 py-2 rounded bg-purple-600 text-white ${!merchantId? 'opacity-60':''}`} disabled={!merchantId || cvLoading} onClick={runCvTop}>{cvLoading? 'Running…':'Top Merchants'}</button>
              <button className={`px-3 py-2 rounded bg-slate-700 text-white ${!merchantId? 'opacity-60':''}`} disabled={!merchantId || cvLoading} onClick={runCvBenchmark}>{cvLoading? 'Running…':'Benchmark'}</button>
            </div>
            <div className="flex gap-2">
              <button className={`px-3 py-2 rounded bg-emerald-600 text-white ${!merchantId? 'opacity-60':''}`} disabled={!merchantId || cvSnapLoading} onClick={runSnapshotCvCompetitors}>{cvSnapLoading? 'Saving…':'Save CV Competitors Snapshot'}</button>
              <button className={`px-3 py-2 rounded bg-teal-700 text-white ${!merchantId? 'opacity-60':''}`} disabled={!merchantId || cvSnapTopLoading} onClick={runSnapshotCvTopMerchants}>{cvSnapTopLoading? 'Saving…':'Save CV Top Merchants Snapshot'}</button>
              <button className={`px-3 py-2 rounded bg-sky-700 text-white ${!merchantId? 'opacity-60':''}`} disabled={!merchantId || cvSnapLoading} onClick={runSnapshotCvCompetitorsAuto}>{cvSnapLoading? 'Saving…':'Save CV Competitors (Auto cats)'}</button>
              <button className={`px-3 py-2 rounded bg-indigo-700 text-white ${!merchantId? 'opacity-60':''}`} disabled={!merchantId || cvSnapTopLoading} onClick={runSnapshotCvTopMerchantsAuto}>{cvSnapTopLoading? 'Saving…':'Save CV Top Merchants (Auto cats)'}</button>
            </div>
          </div>

          {cvError ? <div className="text-xs text-red-600 mb-2">{cvError}</div> : null}

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-medium mb-1">Competitors</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead><tr className="bg-gray-50"><th className="px-2 py-1">rank</th><th className="px-2 py-1">domain</th><th className="px-2 py-1">relVis</th></tr></thead>
                  <tbody>
                    {cvCompetitors.map((c,i)=>(<tr key={i} className="border-t"><td className="px-2 py-1 text-right">{c.rank}</td><td className="px-2 py-1">{c.domain}{c.isYourDomain? ' (you)':''}</td><td className="px-2 py-1 text-right">{c.relativeVisibility}</td></tr>))}
                    {!cvCompetitors.length && <tr><td className="px-2 py-2 text-gray-500" colSpan={3}>No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Top Merchants</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead><tr className="bg-gray-50"><th className="px-2 py-1">rank</th><th className="px-2 py-1">domain</th><th className="px-2 py-1">overlap</th></tr></thead>
                  <tbody>
                    {cvTop.map((t,i)=>(<tr key={i} className="border-t"><td className="px-2 py-1 text-right">{t.rank}</td><td className="px-2 py-1">{t.domain}{t.isYourDomain? ' (you)':''}</td><td className="px-2 py-1 text-right">{t.pageOverlapRate}</td></tr>))}
                    {!cvTop.length && <tr><td className="px-2 py-2 text-gray-500" colSpan={3}>No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Benchmark (trend)</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead><tr className="bg-gray-50"><th className="px-2 py-1">date</th><th className="px-2 py-1">you</th><th className="px-2 py-1">category</th></tr></thead>
                  <tbody>
                    {cvBenchmark.map((b,i)=>(<tr key={i} className="border-t"><td className="px-2 py-1">{b.date}</td><td className="px-2 py-1 text-right">{b.yourDomainVisibilityTrend}</td><td className="px-2 py-1 text-right">{b.categoryBenchmarkVisibilityTrend}</td></tr>))}
                    {!cvBenchmark.length && <tr><td className="px-2 py-2 text-gray-500" colSpan={3}>No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* CV Snapshot results */}
        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">
          <div className="text-sm font-medium text-gray-800 mb-2">CV Snapshot Results</div>
          {cvSnapError ? <div className="text-xs text-red-600 mb-2">{cvSnapError}</div> : null}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium mb-1">Competitors Snapshot</div>
              <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(cvSnapResult, null, 2)}</pre>
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Top Merchants Snapshot</div>
              {cvSnapTopError ? <div className="text-xs text-red-600 mb-2">{cvSnapTopError}</div> : null}
              <pre className="text-[11px] bg-gray-50 border rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(cvSnapTopResult, null, 2)}</pre>
            </div>
          </div>
          {!!autoCats.length && (
            <div className="mt-3 text-xs text-gray-600">Auto categories: {autoCats.join(', ')}</div>
          )}
          {!!autoLog.length && (
            <div className="mt-2 text-[11px] bg-gray-50 border rounded-lg p-2">
              {autoLog.map((l, i) => (<div key={i}>{l}</div>))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
